/// <reference types="vite/client" />
import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMsal } from '@azure/msal-react';
import { getMe } from '@/api/auth';
import { listConversations, createConversation } from '@/api/conversations';
import { modelsAPI } from '@/api/models';
import { useAppStore } from '@/store';
import Sidebar from '@/components/Sidebar';
import { ChatContainer } from '@/components/ChatContainer';
import NoAccessPage from '@/components/NoAccessPage';
import Header from '@/components/Header';
import { Model } from '@/models/model';
import { formatNewChatTitle } from '@/utils/dateUtils';
import { 
  GenerateImagePage,
  GenerateVideoPage,
  TranslateDocumentPage,
  TranslateTextPage,
  Onboarding,
  ChatHistoryPage
} from '@/pages';
import AdminPanelPage from '@/pages/AdminPanelPage';
import UserSettingsPage from '@/pages/UserSettingsPage';
import LoginPage from '@/pages/Login';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { useSessionLock } from '@/hooks/useSessionLock';
import SessionLockScreen from '@/components/SessionLockScreen';
import { fetchAndCacheProfileImage } from '@/utils/profileImageCache';
import { queryKeys } from '@/lib/queryKeys';
import { useCurrentUserSettingsQuery } from '@/features/userSettings/hooks/useUserSettingsQuery';
import { useRefreshProfilePictureMutation } from '@/features/userSettings/hooks/useUserSettingsMutations';
import { initializeClientTelemetry, trackNavigationEvent } from '@/telemetry/clientTelemetry';

const App: React.FC = () => {
  const queryClient = useQueryClient();
  const { instance } = useMsal();
  const { refetch: refetchCurrentUserSettings } = useCurrentUserSettingsQuery(false);
  const refreshProfilePictureMutation = useRefreshProfilePictureMutation();
  const location = useLocation();

  // Check for frontend version changes on app mount
  useVersionCheck();

  useEffect(() => {
    initializeClientTelemetry();
  }, []);

  useEffect(() => {
    trackNavigationEvent(location.pathname);
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [noAccessOpen, setNoAccessOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const { isLocked, isPrivacyBlurred, biometricAvailable, unlock } = useSessionLock(isAuthenticated);
  const initializeConversationsPromiseRef = useRef<Promise<void> | null>(null);
  
  const {
  setUser,
  setUserSettings,
    refreshPendingPermissionRequestsCount,
    setAgentList,
    setFeatureList,
    setModelList,
    setConversationList,
    setSelectedConversation,
    currentFeature,
    user,
    userSettings
  } = useAppStore();

  useEffect(() => {
    if (!isAuthenticated || !userSettings) {
      return;
    }

    const canReviewPermissionRequests =
      userSettings.role === 'admin' || userSettings.role === 'manager' || userSettings.is_admin === true;

    if (canReviewPermissionRequests) {
      void refreshPendingPermissionRequestsCount();
    }
  }, [
    isAuthenticated,
    userSettings?.id,
    userSettings?.role,
    userSettings?.is_admin,
    refreshPendingPermissionRequestsCount,
  ]);

  const fetchConversationList = async () => {
    return queryClient.fetchQuery({
      queryKey: queryKeys.conversations.list(),
      queryFn: listConversations,
      staleTime: 0,
    });
  };

  const refreshConversationList = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.conversations.list() });
    return fetchConversationList();
  };

  const initializeConversations = async () => {
    try {
      // Import listMessages to check if conversations have messages
      const { listMessages } = await import('./api/messages');
      
      // First check if we already have conversations loaded
      let conversations = await fetchConversationList();
      
      // Only create a new conversation if we truly have none
      if (conversations.length === 0) {
        try {
          const newConversation = await createConversation({ title: formatNewChatTitle() });
          conversations = [newConversation]; // Use the returned conversation directly
        } catch (createError) {
          console.error('Error creating new conversation:', createError);
          // Re-fetch conversations in case there was a race condition
          conversations = await refreshConversationList();
        }
      }
      
      // Helper to set model from conversation or default
      const setModelForConversation = (conversation: any) => {
        const currentState = useAppStore.getState();
        const modelList = currentState.modelList;
        const userSettings = currentState.userSettings;
        
        // Try conversation's last_used_model first
        if (conversation?.last_used_model) {
          const isModelAccessible = modelList.some((m: any) => m.key === conversation.last_used_model);
          if (isModelAccessible) {
            currentState.setSelectedModel(conversation.last_used_model);
            return;
          }
        }
        
        // Fall back to user's default_model
        if (userSettings?.default_model) {
          const isModelAccessible = modelList.some((m: any) => m.key === userSettings.default_model);
          if (isModelAccessible) {
            currentState.setSelectedModel(userSettings.default_model);
          }
        }
      };
      
      // Only update states if we still have a valid response
      if (Array.isArray(conversations)) {
        setConversationList(conversations);
        
        const currentState = useAppStore.getState();
        
        // Only proceed if we don't have a feature selected and no conversation is currently selected
        if (!currentState.currentFeature && !currentState.selectedConversation) {
          // Get the most recent conversation (first one after sorting by updated_date)
          const mostRecentConversation = conversations[0];
          
          if (mostRecentConversation) {
            try {
              // Check if the most recent conversation has any messages
              const messages = await listMessages(mostRecentConversation.id);
              
              if (messages.length === 0) {
                // No messages - update the timestamp to make it the most recent, then select it
                const { updateConversation } = await import('./api/conversations');
                await updateConversation(mostRecentConversation.id, {});
                
                // Refresh conversation list to get the updated timestamp
                const updatedConversations = await refreshConversationList();
                setConversationList(updatedConversations);
                
                // Select the updated conversation
                const updatedMostRecent = updatedConversations[0];
                setSelectedConversation(updatedMostRecent);
                setModelForConversation(updatedMostRecent);
              } else {
                // Most recent conversation has messages — look for an existing empty chat
                // to reuse before creating a new one (mirrors Sidebar.handleNewChat logic)
                const existingEmptyChat = conversations.find(
                  conv => conv.id !== mostRecentConversation.id &&
                    (conv.message_count === 0 || conv.message_count === undefined)
                );

                if (existingEmptyChat) {
                  // Reuse the existing empty conversation and refresh timestamp
                  // so it behaves like a newly opened chat.
                  const { updateConversation } = await import('./api/conversations');
                  await updateConversation(existingEmptyChat.id, {});

                  const updatedConversations = await refreshConversationList();
                  setConversationList(updatedConversations);

                  const refreshedEmptyChat =
                    updatedConversations.find((conv) => conv.id === existingEmptyChat.id) ||
                    existingEmptyChat;

                  setSelectedConversation(refreshedEmptyChat);
                  setModelForConversation(refreshedEmptyChat);
                } else {
                  // No empty chat available — create a fresh one
                  const newConversation = await createConversation({ title: formatNewChatTitle() });
                  const updatedConversations = await refreshConversationList();
                  setConversationList(updatedConversations);
                  setSelectedConversation(newConversation);
                  setModelForConversation(newConversation);

                  // Update title of the previous conversation in the background (non-blocking)
                  // This triggers title generation for the conversation we're leaving
                  const { selectConversation } = await import('./api/conversations');
                  selectConversation(newConversation.id, mostRecentConversation.id)
                    .then(() => refreshConversationList())
                    .then((refreshedConversations) => {
                      setConversationList(refreshedConversations);
                    })
                    .catch((error) => {
                      console.error('Failed to update previous conversation title:', error);
                    });
                }
              }
            } catch (error) {
              console.error('Error checking message count:', error);
              // Fallback: select the most recent conversation
              setSelectedConversation(mostRecentConversation);
              setModelForConversation(mostRecentConversation);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error initializing conversations:', error);
    }
  };

  const initializeConversationsSingleFlight = async () => {
    if (initializeConversationsPromiseRef.current) {
      await initializeConversationsPromiseRef.current;
      return;
    }

    const inFlight = initializeConversations()
      .catch((error) => {
        console.error('Error in single-flight conversation initialization:', error);
      })
      .finally(() => {
        initializeConversationsPromiseRef.current = null;
      });

    initializeConversationsPromiseRef.current = inFlight;
    await inFlight;
  };

  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      try {
        // Process any pending MSAL redirect (must resolve before API calls)
        await instance.handleRedirectPromise();

        try {
          // Get user info first
          const userRes = await getMe();
          if (!mounted) return;

          // Set authentication state to true since we have a user
          setIsAuthenticated(true);

          // Extract canonical user ID (backend normalizes to `id`);
          // fall back to oid/sub for older sessions
          const userId = userRes.user.id || userRes.user.oid || userRes.user.sub;

          // Then get user settings
          const userSettingsResult = await refetchCurrentUserSettings();
          const cfg = userSettingsResult.data;
          if (!cfg) {
            throw new Error('Failed to load user settings');
          }
          if (!mounted) return;

          // Store user settings in state
          setUserSettings(cfg);

          if (cfg.role === 'admin' || cfg.role === 'manager' || cfg.is_admin === true) {
            void refreshPendingPermissionRequestsCount();
          }

          // Initialize theme from backend preference if available
          if (cfg.theme_preference === 'dark' || cfg.theme_preference === 'light') {
            try {
              // Use store setter to persist UI state (it will not re-persist if same)
              useAppStore.getState().setThemeMode(cfg.theme_preference);
            } catch (err) {
              console.error('Failed to set theme from user settings:', err);
            }
          }

          // Refresh profile picture version to ensure fresh image after deployment
          try {
            const updatedSettings = await refreshProfilePictureMutation.mutateAsync();
            if (!mounted) return;

            // Fetch and cache profile image in localStorage
            if (userId) {
              await fetchAndCacheProfileImage(userId, true); // Force refresh on login
            }

            // Update user object with admin status and refreshed picture_version
            setUser({ 
              name: userRes.user.name || userRes.user.email,
              picture: userRes.user.picture,
              isAdmin: updatedSettings.role === 'admin' || updatedSettings.role === 'manager' || updatedSettings.is_admin === true,
              id: userId,
              pictureVersion: updatedSettings.picture_version || Date.now()
            });
          } catch (error) {
            // Fallback to using existing settings if refresh fails
            setUser({ 
              name: userRes.user.name || userRes.user.email,
              picture: userRes.user.picture,
              isAdmin: cfg.role === 'admin' || cfg.role === 'manager' || cfg.is_admin === true,
              id: userId,
              pictureVersion: cfg.picture_version || Date.now()
            });
          }

          if (cfg.agent_access) {
            // Always include 'default' as the first agent, then add others (excluding 'default')
            const agents = [
              ...cfg.agent_access
                .filter(id => id !== 'default')
                .map(id => ({ id, name: id.charAt(0).toUpperCase() + id.slice(1) }))
          ];
          setAgentList(agents);
          }

          // Agent and Feature access logic (v3 permissions-based):
          // Use the /api/agents/accessible and /api/features/accessible endpoints
          try {
            const { fetchAccessibleAgents, fetchAccessibleFeatures } = await import('./api/models');
            
            // Fetch accessible agents - include all fields (id, name, icon, description)
            const accessibleAgents = await fetchAccessibleAgents();
            setAgentList(accessibleAgents.map((a: any) => ({ 
              id: a.id, 
              name: a.name,
              icon: a.icon,
              description: a.description 
            })));
            // console.log(`Loaded ${accessibleAgents.length} accessible agents`);
            
            // Fetch accessible features - pass through all fields
            const accessibleFeatures = await fetchAccessibleFeatures();
            setFeatureList(accessibleFeatures);
            // console.log(`Loaded ${accessibleFeatures.length} accessible features`);
          } catch (error) {
            console.error('Error fetching accessible agents/features:', error);
            setAgentList([]);
            setFeatureList([]);
          }

          // Model access logic (v3 permissions-based):
          // Use the /api/models/accessible endpoint which returns models that are either:
          // 1. Implicit (is_implicit=true) - available to all users
          // 2. Explicitly granted via permissions array (model:<model-id>)
          
          try {
            // Fetch accessible models for the current user
            const accessibleModels = await modelsAPI.getAccessibleModels();
            
            const userModels: Model[] = accessibleModels.map((m: any) => ({
              id: m.id,
              name: m.name,
              description: m.description,
              supports_images: !!m.supports_images,
            }));
            
            // const implicitCount = accessibleModels.filter((m: any) => m.is_implicit).length;
            // const explicitCount = accessibleModels.length - implicitCount;
            
            setModelList(userModels);
            // console.log(`Loaded ${userModels.length} accessible models (${implicitCount} implicit + ${explicitCount} with explicit permissions)`);
          } catch (error) {
            console.error('Error fetching accessible models:', error);
            // Fallback to implicit models only
            try {
              const implicitModels = await modelsAPI.getImplicitModels();
              const modelOptions: Model[] = implicitModels.map((m: any) => ({ 
                id: m.id, 
                name: m.name,
                description: m.description,
                supports_images: !!m.supports_images,
              }));
              setModelList(modelOptions);
              console.debug(`Loaded ${modelOptions.length} implicit models (fallback)`);
            } catch (fallbackError) {
              console.error('Error fetching implicit models:', fallbackError);
            }
          }

          // Initialize conversations if user has completed onboarding
          if (mounted && cfg.onboarding_completed !== false) {
            await initializeConversationsSingleFlight();
          }
        } catch (authError) {
          // Re-throw non-authentication errors (e.g. NO_BOLT_ROLE 403) so the
          // outer catch can open the NoAccessPage instead of going to login.
          const msg = (authError as any)?.toString?.() ?? '';
          if (!msg.includes('401') && !msg.includes('Not authenticated')) {
            throw authError;
          }
          setIsAuthenticated(false);
        }
      } catch (error) {
        // Check if it's an authentication error
        if (typeof error === 'object' && error !== null && 'toString' in error) {
          const errorString = error.toString();
          if (errorString.includes('401') || errorString.includes('Not authenticated')) {
            setIsAuthenticated(false);
            window.location.href = '/login';
            return;
          } else {
            if (mounted) {
              setNoAccessOpen(true);
            }
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, []);

  // When the user navigates back to '/' with no conversation selected (e.g. after
  // returning from the Search/History page having deleted all chats, or after any
  // operation that clears selectedConversation), auto-select the most recent conversation
  // or create a new one if the list is empty.
  useEffect(() => {
    if (location.pathname !== '/') return;
    if (loading) return;
    if (!isAuthenticated) return;
    if (userSettings?.onboarding_completed === false) return;
    const state = useAppStore.getState();
    if (state.selectedConversation || state.currentFeature) return;
    const first = state.conversationList[0];
    if (first) {
      state.setSelectedConversation(first);
    } else {
      // All conversations were deleted — bootstrap a fresh empty chat.
      void initializeConversationsSingleFlight();
    }
  }, [location.pathname, loading, isAuthenticated, userSettings?.onboarding_completed]);

  if (loading) {
    return (
      <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center" sx={{ bgcolor: 'background.default' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Show NoAccessPage overlay before any authenticated layout so it is
  // displayed even when the user failed the App Role gate (403 NO_BOLT_ROLE).
  if (noAccessOpen) {
    return <NoAccessPage open={true} onClose={() => {}} />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // Show onboarding page if user hasn't completed onboarding (Increment 3)
  if (isAuthenticated && userSettings && userSettings.onboarding_completed === false) {
    return (
      <Box
        width="100%"
        height="100dvh"
        sx={{ 
          bgcolor: 'background.default',
          overflowY: 'auto',
        }}
      >
        <Onboarding />
      </Box>
    );
  }

  return (
    <>
      <NoAccessPage open={noAccessOpen} onClose={() => {}} />
      {/* A4: Opaque privacy cover — shown immediately when backgrounded or locked.
          Sits below the lock screen (z-index 9998 vs 9999) but above all app content.
          No CSS transition so it renders in the same frame as the state change,
          maximising the chance of covering content before the OS app-switcher snapshot. */}
      {isPrivacyBlurred && (
        <Box
          aria-hidden="true"
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9998,
            bgcolor: 'primary.main',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 3,
          }}
        >
          <Box
            component="img"
            src="/images/Bolt.png"
            alt=""
            sx={{ height: 48, filter: 'brightness(0) invert(1)' }}
          />
        </Box>
      )}
      {/* Disable interaction on the entire layout while locked */}
      <Box
        width="100%"
        height="100dvh"
        display="flex"
        flexDirection="column"
        sx={{
          bgcolor: 'background.default',
          pointerEvents: isLocked ? 'none' : 'auto',
          userSelect: isLocked ? 'none' : 'auto',
        }}
      >
        {/* Header */}
        <Header user={user} />
        
        {/* Content Area */}
        <Box width="100%" flex={1} minHeight={0} overflow="hidden" display="flex" boxShadow={8}>
          {/* Conditionally render Sidebar - hide it on admin, user-settings, and chat-history pages */}
          {location.pathname !== '/admin' && location.pathname !== '/user-settings' && location.pathname !== '/chat-history' && (
            <Box height="100%" display="flex" flexDirection="column" sx={{ flexShrink: 0, overflow: 'visible' }}>
              <Sidebar user={user} hasModelAccess={!noAccessOpen} />
            </Box>
          )}
          <Box sx={{ 
            flex: 1,
            display: 'flex', 
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
          }}>
            <Box flex={1} minHeight={0} position="relative" overflow="auto">
              <Routes>
                <Route path="/admin" element={<AdminPanelPage />} />
                <Route path="/user-settings" element={<UserSettingsPage />} />
                <Route path="/chat-history" element={<ChatHistoryPage />} />
                <Route path="/" element={
                  currentFeature === 'generate-image' ? (
                    <GenerateImagePage />
                  ) : currentFeature === 'generate-video' ? (
                    <GenerateVideoPage />
                  ) : currentFeature === 'translate-document' ? (
                    <TranslateDocumentPage />
                  ) : currentFeature === 'translate-text' ? (
                    <TranslateTextPage />
                  ) : (
                    <ChatContainer />
                  )
                } />
              </Routes>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* A2/A3/A4: Lock screen — shown only once MSAL has a valid token.
          isLocked may be true from sessionStorage immediately on reload, but
          the biometric API calls need an access token. Gating on isAuthenticated
          ensures MSAL finishes its silent refresh before the lock screen appears.
          The privacy cover (isPrivacyBlurred) still hides content during the gap. */}
      <SessionLockScreen
        isVisible={isLocked && isAuthenticated}
        biometricAvailable={biometricAvailable}
        userName={user?.name}
        onUnlock={unlock}
      />
    </>
  );
}

export default App;